import React, { useEffect, useState, useContext } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { documentsApi, DOCUMENT_TYPE_LABELS, BLOCKCHAIN_STATUS } from '../api/documents';
import { bunkeringApi } from '../api/bunkering';
import {
  Container, Typography, Button, Box,
  CircularProgress, Alert, Chip, Divider, Grid, Paper, Stack
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VerifiedIcon from '@mui/icons-material/Verified';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import FingerPrintIcon from '@mui/icons-material/Fingerprint';
import LinkIcon from '@mui/icons-material/Link';
import ReplayIcon from '@mui/icons-material/Replay';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

export default function DocumentDetails() {
  const { documentId } = useParams();
  const { user } = useContext(AuthContext);
  const [document, setDocument] = useState(null);
  const [bunkering, setBunkering] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vesselLoading, setVesselLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState(null);
  const [confirmations, setConfirmations] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [resubmitLoading, setResubmitLoading] = useState(false);
  const [resubmitError, setResubmitError] = useState('');
  const [resubmitSuccess, setResubmitSuccess] = useState(false);

  useEffect(() => {
    const fetchDocument = async () => {
      setLoading(true);
      setError('');
      try {
        const resp = await documentsApi.getDocument(documentId);
        const documentData = resp.data.data;
        setDocument(documentData);
        setLoading(false);
        // Fetch bunkering details to get vessel information
        if (documentData.bunkering_id) {
          setVesselLoading(true);
          try {
            const bunkeringResp = await bunkeringApi.getBunkering(documentData.bunkering_id);
            setBunkering(bunkeringResp.data.data);
          } catch (vesselErr) {
            console.error('Failed to load vessel details:', vesselErr);
            // Don't show error for vessel loading failure - just keep vessel info empty
          } finally {
            setVesselLoading(false);
          }
        }
      } catch (err) {
        console.error('Failed to load document details:', err);
        if (err.response && err.response.data) {
          setError(err.response.data.detail || 'Failed to load document.');
        } else {
          setError('Failed to load document.');
        }
        setLoading(false);
      }
    };
    fetchDocument();
  }, [documentId]);

  const handleViewPdf = async () => {
    if (!document) return;
    try {
      const response = await documentsApi.downloadDocument(document.id);
      const file = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(file);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Failed to download PDF:', err);
      setError('Failed to load PDF.');
    }
  };

  const handleCheckStatus = async () => {
    setStatusLoading(true);
    setStatusError('');
    try {
      const resp = await documentsApi.getDocumentStatus(document.id);
      const data = resp.data.data;
      setStatus(data.status);
      setConfirmations(data.confirmations !== undefined ? data.confirmations : null);
    } catch (err) {
      console.error('Failed to fetch transaction status:', err);
      const detail = err.response?.data?.detail || 'Failed to fetch transaction status.';
      setStatusError(detail);
    } finally {
      setStatusLoading(false);
    }
  };
  
  const handleResubmit = async () => {
    setResubmitLoading(true);
    setResubmitError('');
    setResubmitSuccess(false);
    
    try {
      const resubmitResp = await documentsApi.resubmitDocument(document.id);
      setResubmitSuccess(true);
      
      // Update document with the latest blockchain info from resubmit response
      setDocument(prev => ({
        ...prev,
        blockchain_tx_id: resubmitResp.data.data.blockchain_tx_id,
        blockchain_submission_time: resubmitResp.data.data.blockchain_submission_time,
        blockchain_explorer_url: resubmitResp.data.data.blockchain_explorer_url
      }));
      
      // Update the status after resubmission
      await handleCheckStatus();
    } catch (err) {
      console.error('Failed to resubmit document:', err);
      const detail = err.response?.data?.detail || 'Failed to resubmit document to blockchain.';
      setResubmitError(detail);
    } finally {
      setResubmitLoading(false);
    }
  };

  const handleViewTransaction = () => {
    if (document?.blockchain_explorer_url) {
      window.open(document.blockchain_explorer_url, '_blank', 'noopener,noreferrer');
    }
  };

  // Check if user can resubmit (only original uploader)
  const canResubmit = () => {
    return user && 
           document?.uploaded_by?.id === user.id &&
           (user.role === 'SHIPOWNER' || user.role === 'SUPPLIER');
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
      <CircularProgress />
    </Box>
  );
  
  if (error) return (
    <Container maxWidth="md" sx={{ my: 4 }}>
      <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>
    </Container>
  );
  
  if (!document) return null;

  // Define status color and icon
  const getStatusChip = () => {
    if (!status) return null;
    
    const statusProps = {
      [BLOCKCHAIN_STATUS.CONFIRMED]: { color: 'success', icon: <VerifiedIcon fontSize="small" /> },
      [BLOCKCHAIN_STATUS.CONFIRMING]: { color: 'warning', icon: null },
      [BLOCKCHAIN_STATUS.UNCONFIRMED]: { color: 'warning', icon: null },
      [BLOCKCHAIN_STATUS.NOT_BUNDLED]: { color: 'warning', icon: null },
      [BLOCKCHAIN_STATUS.QUEUED]: { color: 'info', icon: null },
      [BLOCKCHAIN_STATUS.FAILED]: { color: 'error', icon: null }
    };
    
    const props = statusProps[status] || { color: 'info', icon: null };
    
    return (
      <Chip 
        label={status}
        color={props.color}
        size="small"
        icon={props.icon}
        sx={{ fontWeight: 500 }}
      />
    );
  };

  return (
    <Container maxWidth="md" sx={{ my: 5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          component={RouterLink}
          to={`/bunkerings/${document.bunkering_id}`}
          variant="outlined"
          sx={{ 
            textTransform: 'none', 
            borderRadius: '8px',
            fontSize: '0.9rem'
          }}
        >
          Back to Bunkering Details
        </Button>
        
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleViewPdf}
          sx={{ 
            textTransform: 'none', 
            borderRadius: '8px',
            fontSize: '0.9rem',
            boxShadow: 2
          }}
        >
          View PDF
        </Button>
      </Box>

      <Paper 
        elevation={3} 
        sx={{ 
          borderRadius: '12px',
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{ 
            bgcolor: 'primary.main', 
            py: 2, 
            px: 3, 
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <InsertDriveFileIcon />
          <Typography variant="h5" fontWeight="500">
            Document Details
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Document ID */}
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FingerPrintIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">ID</Typography>
              </Stack>
              <Typography variant="body1" fontWeight="500" sx={{ ml: 3.5 }}>
                {document.id}
              </Typography>
            </Grid>

            {/* Document Type */}
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <InsertDriveFileIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">Document Type</Typography>
              </Stack>
              <Box sx={{ ml: 3.5 }}>
                <Chip 
                  label={DOCUMENT_TYPE_LABELS[document.doc_type] || document.doc_type}
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              </Box>
            </Grid>

            {/* Filename */}
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <InsertDriveFileIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">Filename</Typography>
              </Stack>
              <Typography variant="body1" fontWeight="500" sx={{ ml: 3.5 }}>
                {document.filename}
              </Typography>
            </Grid>

            {/* Upload Date */}
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarTodayIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">Upload Date</Typography>
              </Stack>
              <Typography variant="body1" fontWeight="500" sx={{ ml: 3.5 }}>
                {document.upload_date ? new Date(document.upload_date).toLocaleString() : 'N/A'}
              </Typography>
            </Grid>

            {/* Uploaded By */}
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PersonIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">Uploaded By</Typography>
              </Stack>
              <Box sx={{ ml: 3.5 }}>
                <Chip 
                  label={`${document.uploaded_by?.name || 'Unknown'} (${document.uploaded_by?.role || 'Unknown'})`} 
                  size="small" 
                  sx={{ fontWeight: 500 }}
                />
              </Box>
            </Grid>

            {/* Vessel Information */}
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <DirectionsBoatIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">Vessel</Typography>
              </Stack>
              <Box sx={{ ml: 3.5 }}>
                {vesselLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">Loading vessel...</Typography>
                  </Box>
                ) : bunkering ? (
                  <Typography variant="body1" fontWeight="500">
                    {bunkering.vessel_name ? `${bunkering.vessel_imo} - ${bunkering.vessel_name}` : bunkering.vessel_imo || 'Unknown vessel'}
                  </Typography>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    Vessel information unavailable
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            {/* PDF Hash */}
            <Grid item xs={12}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FingerPrintIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">PDF Hash (SHA-256)</Typography>
              </Stack>
              <Typography 
                variant="body2" 
                fontFamily="monospace" 
                sx={{ 
                  ml: 3.5, 
                  overflowWrap: 'break-word',
                  bgcolor: 'grey.100',
                  p: 1,
                  borderRadius: 1,
                  mt: 0.5
                }}
              >
                {document.pdf_hash || 'Not available'}
              </Typography>
            </Grid>

            {/* Blockchain Tx */}
            <Grid item xs={12}>
              <Stack direction="row" spacing={1} alignItems="center">
                <LinkIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">Blockchain Transaction</Typography>
              </Stack>
              <Box sx={{ ml: 3.5, mt: 0.5 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography 
                    variant="body2" 
                    fontFamily="monospace" 
                    sx={{ 
                      overflowWrap: 'break-word',
                      bgcolor: document.blockchain_tx_id ? 'grey.100' : 'transparent',
                      p: document.blockchain_tx_id ? 1 : 0,
                      borderRadius: 1,
                      flexGrow: 1
                    }}
                  >
                    {document.blockchain_tx_id || 'Pending'}
                  </Typography>
                  {document.blockchain_explorer_url && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleViewTransaction}
                      startIcon={<OpenInNewIcon />}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        minWidth: 'auto'
                      }}
                    >
                      View on Blockchain
                    </Button>
                  )}
                </Stack>
              </Box>
            </Grid>
            
            {/* Blockchain Submission Time */}
            {document.blockchain_submission_time && (
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CalendarTodayIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" color="text.secondary">Blockchain Submission Time</Typography>
                </Stack>
                <Typography 
                  variant="body1" 
                  fontWeight="500" 
                  sx={{ ml: 3.5 }}
                >
                  {new Date(document.blockchain_submission_time).toLocaleString()}
                </Typography>
              </Grid>
            )}

            {/* Blockchain Status */}
            <Grid item xs={12}>
              <Box sx={{ mt: 2, ml: 3.5 }}>
                <Button
                  variant="outlined"
                  onClick={handleCheckStatus}
                  disabled={statusLoading}
                  data-testid="check-status-button"
                  startIcon={statusLoading ? <CircularProgress size={16} /> : null}
                  sx={{ 
                    textTransform: 'none', 
                    borderRadius: '8px',
                    mr: 2
                  }}
                >
                  {statusLoading ? 'Checking...' : 'Check Blockchain Status'}
                </Button>
                
                {/* Resubmit button - only show if status indicates failure and current user is the original uploader */}
                {(status === BLOCKCHAIN_STATUS.FAILED || status === 'FAILED' || status === 'Missing') && canResubmit() && (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleResubmit}
                    disabled={resubmitLoading}
                    data-testid="resubmit-button"
                    startIcon={resubmitLoading ? <CircularProgress size={16} color="warning" /> : <ReplayIcon />}
                    sx={{ 
                      textTransform: 'none', 
                      borderRadius: '8px'
                    }}
                  >
                    {resubmitLoading ? 'Resubmitting...' : 'Resubmit to Blockchain'}
                  </Button>
                )}
                
                {resubmitError && (
                  <Alert severity="error" sx={{ mt: 2 }}>{resubmitError}</Alert>
                )}
                
                {resubmitSuccess && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Transaction resubmitted successfully.
                  </Alert>
                )}
                
                {statusError && (
                  <Alert severity="error" sx={{ mt: 2 }}>{statusError}</Alert>
                )}
                
                {status && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, gap: 2 }}>
                    {getStatusChip()}
                    {confirmations != null && (
                      <Chip 
                        label={`${confirmations} Confirmations`} 
                        size="small" 
                        variant="outlined" 
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
}